import { StoredScheduleBlock } from './layout/StoredScheduleBlock';

export interface ValidateScheduleBlocksOptions {
  /**
   * When given, the blocks must contain EXACTLY these todo ids (identity
   * preservation): a write that would silently add or drop a block fails.
   * Callers that legitimately add or remove blocks (scheduling/unscheduling an
   * intent) omit this and get uniqueness checks only.
   */
  expectedTodoIds?: string[];
}

export interface ValidateScheduleBlocksResult {
  ok: boolean;
  errors: string[];
}

/**
 * The persistence-boundary invariants for `views.schedule.blocks`. Every write
 * path validates before writing and refuses (throws) instead of persisting a
 * corrupt array — historically, a duplicate start silently deleted an intent on
 * the next rebuild, and a dropped block was deleted permanently. The layout
 * engine keeps these invariants by construction; this guard is defense in depth
 * against any future writer that doesn't.
 */
export function validateScheduleBlocks(
  blocks: StoredScheduleBlock[],
  { expectedTodoIds }: ValidateScheduleBlocksOptions = {},
): ValidateScheduleBlocksResult {
  const errors: string[] = [];

  const todosSeen = new Map<string, number>();
  const startsSeen = new Map<string, string>();

  for (const block of blocks) {
    if (!block.todo) {
      errors.push(`block missing todo id: ${JSON.stringify(block)}`);
      continue;
    }

    todosSeen.set(`${block.todo}`, (todosSeen.get(`${block.todo}`) ?? 0) + 1);

    if (block.start != null) {
      if (!/^\d{1,2}:\d{2}(:\d{2})?$/.test(`${block.start}`)) {
        errors.push(`block '${block.todo}' has an unparseable start: '${block.start}'`);
      } else {
        const existing = startsSeen.get(`${block.start}`);

        if (existing) {
          errors.push(
            `duplicate start ${block.start}: '${existing}' and '${block.todo}' — a ` +
            `duplicate start historically deleted an intent on the next rebuild`
          );
        }
        startsSeen.set(`${block.start}`, `${block.todo}`);
      }
    }
  }

  todosSeen.forEach((count, todo) => {
    if (count > 1) {
      errors.push(`todo '${todo}' appears ${count} times`);
    }
  });

  if (expectedTodoIds) {
    const actual: string[] = [];
    todosSeen.forEach((_count, todo) => actual.push(todo));
    actual.sort();
    const expected = [...expectedTodoIds].map(String).sort();
    const missing = expected.filter(id => !actual.includes(id));
    const added = actual.filter(id => !expected.includes(id));

    if (missing.length > 0) {
      errors.push(`write would DROP block(s): ${missing.join(', ')}`);
    }
    if (added.length > 0) {
      errors.push(`write would ADD unexpected block(s): ${added.join(', ')}`);
    }
  }

  return { ok: errors.length === 0, errors };
}
